import React from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Status from '../../../../../../../../atom/Status';
import { Body } from '~/components/atom/Typography';
import AppointmentDateLabel from './components/AppointmentDateLabel';
import styled, { css } from 'styled-components';
import {
  GetLfaConversionFilterType,
  Lfa_Conversion_LastServiceFeedback_Status,
} from '~/graphql/types';
import getAddressString from '~/components/page/LFA/utils/getAddressString';
import getFrontendStatus from '~/components/page/LFA/utils/getFrontendStatus';
import Service from '../../../../../Service';
import StatusDescription from './components/StatusDescription';
import hasValue from '~/util/hasValue';
import {
  cardStatusLabelsMap,
  statusColorMap,
} from '~/components/page/LFA/constants';
import getDateLabelByStatus from '~/components/page/LFA/utils/getCardDateLabelByStatus';
import Note from './components/Note';
import type { ClientLfaConversion } from '~/components/page/LFA/types';

type Props = {
  dataTestId?: string;
  className?: string;
  conversion: Omit<
    ClientLfaConversion,
    'Activities' | 'initialSelectedServices'
  >;

  selected: boolean;
  filter: GetLfaConversionFilterType;
  onClick: () => void;
};

const Card: React.FCC<Props> = ({
  dataTestId,
  className,
  conversion,
  selected = false,
  onClick,
  filter,
}) => {
  const {
    name,
    address,
    selectedServices,
    appointmentDate,
    autoAcceptDate,
    noteInfo,
    daysToAutoAcceptance,
    status,
    finalFeedbackGivenDate,
    lastServiceFeedback,
    lastServiceFeedbackStatus,
    feedbackReminderDate,
  } = conversion;

  const frontendStatus = getFrontendStatus({
    status,
    appointmentDate,
    daysToAutoAccept: daysToAutoAcceptance,
    hasFinalFeedback: hasValue(finalFeedbackGivenDate),
    hasServiceFeedback: hasValue(lastServiceFeedback),
  });

  const dateTimeLabel = getDateLabelByStatus({
    lastServiceFeedbackStatus,
    feedbackReminderDate,
    appointmentDate,
    filter,
    frontendStatus,
    finalFeedbackGivenDate,
  });

  const lastNote = noteInfo.lastNote;

  return (
    <Container
      onClick={onClick}
      dataTestId={dataTestId}
      className={className}
      width="100%"
      border={{
        radius: 'base',
        width: 's',
        color: { group: selected ? 'primary' : 'tertiary' },
      }}
      direction="column"
      $selected={selected}
    >
      <JustificationContainer width="100%" padding={['base', 'm', 'xs', 'm']}>
        <JustificationContainer direction="column" gap="xxs" width="100%">
          <Status
            textColor={statusColorMap[frontendStatus].text}
            backgroundColor={statusColorMap[frontendStatus].background}
            label={cardStatusLabelsMap(
              frontendStatus,
              daysToAutoAcceptance,
              lastServiceFeedbackStatus
                ? lastServiceFeedbackStatus ===
                    Lfa_Conversion_LastServiceFeedback_Status.Snoozed
                : false,
            )}
          />
          <NameAddressContainer direction="column">
            <Body margin={[null]} size="base" fontWeight="semiBold">
              {name}
            </Body>
            {address && (
              <Body margin={[null]} size="s">
                {getAddressString(address)}
              </Body>
            )}
          </NameAddressContainer>

          <AppointmentDateLabel labelWithStatus={dateTimeLabel} />
          {lastNote && <Note note={lastNote} />}
        </JustificationContainer>

        <JustificationContainer direction="column" gap="xxs" align="end">
          {selectedServices.map(service => (
            <Service key={service.id} label={service.name}>
              {service.description}
            </Service>
          ))}
        </JustificationContainer>
      </JustificationContainer>
      <StatusDescription
        frontendStatus={frontendStatus}
        autoAcceptDate={autoAcceptDate}
        noteInfo={noteInfo}
        finalFeedbackGivenDate={finalFeedbackGivenDate}
      />
    </Container>
  );
};

const Container = styled(JustificationContainer)<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    transition: all 0.3s ease-out;
    cursor: pointer;
    overflow-x: visible;

    ${$selected &&
    css`
      box-shadow: ${theme.getTokens().boxShadow.base};
      transform: scale(1.01);
    `}

    &:hover {
      border-color: ${theme.color('primary')};
      transform: scale(1.01);
    }
  `,
);

const NameAddressContainer = styled(JustificationContainer)`
  gap: 4px;
`;

export default Card;
